"use client";

/**
 * V2 login page — /v2/login (additive, pre-cutover Studio auth stays untouched).
 *
 * Authenticates against V2's OWN self-hosted spine (/v2/api/auth/login), which
 * mints the HS256 `cv2_session` cookie. Ported from V1's web/app/login/page.tsx
 * (CredentialsProvider + GoogleProvider) but rewired:
 *   - credentials → POST /v2/api/auth/login  (returns {user} on 200, {error} on 401)
 *   - Google      → Google Identity Services → POST /v2/api/auth/google {id_token}
 * V1's `cosmic-*` Tailwind tokens are mapped to V2 shadcn tokens.
 *
 * basePath note: V2's auth routes are served under /v2/api/auth/* and raw
 * fetch() is NOT basePath-rewritten, so we call the explicit /v2/... path (same
 * convention as use-session.ts). Redirects use next/navigation useRouter, which
 * IS basePath-aware, so router.push("/generate") lands on /v2/generate.
 */

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { ChittravanLogoMark } from "@/components/marketing/logo-mark";
import { SIGNUP_CREDITS } from "@/lib/product-facts";

const AUTH_BASE = "/api/auth";
const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "";
const GSI_SRC = "https://accounts.google.com/gsi/client";

/**
 * Sanitize a `callbackUrl` query param to an in-app path. We only allow paths
 * that begin with "/v2" (a V2-internal route) OR a bare relative app path
 * ("/something" but NOT "//host" protocol-relative and NOT "http(s)://..."),
 * which next/navigation will basePath-prefix to /v2. Anything else (absolute
 * URLs, protocol-relative, javascript:) falls back to the default — this blocks
 * open-redirect attacks via the login URL.
 */
function sanitizeCallback(raw: string | null): string {
	if (!raw) return "/generate";
	// Must be a same-origin absolute path. Reject protocol-relative ("//evil.com"),
	// backslash-tricks ("/\evil.com"), and absolute/scheme URLs ("http://", "javascript:").
	if (!raw.startsWith("/")) return "/generate";
	if (raw.startsWith("//") || raw.startsWith("/\\")) return "/generate";
	// Already basePath-qualified — router.push would double-prefix it, so strip /v2.
	if (raw === "/v2" || raw.startsWith("/v2/")) {
		const stripped = raw.slice("/v2".length) || "/";
		return stripped.startsWith("/") ? stripped : `/${stripped}`;
	}
	return raw;
}

const FEATURES = [
	{
		title: "AI-Powered Pipeline",
		desc: "From idea to finished video — script, voice, visuals, assembled.",
	},
	{
		title: "Professional Studio",
		desc: "Full timeline editor. Bring your assets or generate everything with AI.",
	},
	{
		title: "AI Voice & Clips",
		desc: "16 voices, expressive emotion models, and cinematic AI-generated clips.",
	},
	{
		title: "Smart Scoring",
		desc: "AI reviews every asset — hook, pacing, story arc, audio quality.",
	},
];

declare global {
	interface Window {
		google?: {
			accounts?: {
				id?: {
					initialize: (config: {
						client_id: string;
						callback: (resp: { credential?: string }) => void;
					}) => void;
					renderButton: (
						el: HTMLElement,
						options: Record<string, unknown>,
					) => void;
				};
			};
		};
	}
}

function LoginInner() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const callbackUrl = sanitizeCallback(searchParams.get("callbackUrl"));

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [googleLoading, setGoogleLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const googleBtnRef = useRef<HTMLDivElement | null>(null);

	const handleGoogleCredential = useCallback(
		async (idToken: string) => {
			setGoogleLoading(true);
			setError(null);
			try {
				const res = await fetch(`${AUTH_BASE}/google`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ id_token: idToken }),
				});
				if (!res.ok) {
					const d = await res.json().catch(() => ({}));
					throw new Error(d.detail ?? "Google sign-in failed.");
				}
				router.push(callbackUrl);
			} catch (err) {
				setGoogleLoading(false);
				setError(
					err instanceof Error ? err.message : "Google sign-in failed.",
				);
			}
		},
		[router, callbackUrl],
	);

	// Load Google Identity Services and render the official button. If the
	// client id is empty (not provided at build time — see report), we skip
	// initialisation entirely so the page still works for credentials login.
	useEffect(() => {
		if (!GOOGLE_CLIENT_ID) return;

		let cancelled = false;

		function init() {
			if (cancelled) return;
			const gid = window.google?.accounts?.id;
			if (!gid || !googleBtnRef.current) return;
			gid.initialize({
				client_id: GOOGLE_CLIENT_ID,
				callback: (resp) => {
					if (resp.credential) void handleGoogleCredential(resp.credential);
				},
			});
			gid.renderButton(googleBtnRef.current, {
				theme: "outline",
				size: "large",
				width: 320,
				text: "continue_with",
				logo_alignment: "left",
			});
		}

		const existing = document.querySelector<HTMLScriptElement>(
			`script[src="${GSI_SRC}"]`,
		);
		if (existing) {
			if (window.google?.accounts?.id) init();
			else existing.addEventListener("load", init, { once: true });
			return () => {
				cancelled = true;
			};
		}

		const script = document.createElement("script");
		script.src = GSI_SRC;
		script.async = true;
		script.defer = true;
		script.addEventListener("load", init, { once: true });
		document.head.appendChild(script);

		return () => {
			cancelled = true;
		};
	}, [handleGoogleCredential]);

	async function handleCredentials(e: React.FormEvent) {
		e.preventDefault();
		setLoading(true);
		setError(null);
		try {
			const res = await fetch(`${AUTH_BASE}/login`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ email, password }),
			});
			if (!res.ok) {
				// Spine collapses all failures to a generic {error} — never leak which field was wrong.
				const d = await res.json().catch(() => ({}));
				throw new Error(d.error ?? "Invalid email or password.");
			}
			router.push(callbackUrl);
		} catch (err) {
			setLoading(false);
			setError(err instanceof Error ? err.message : "Invalid email or password.");
		}
	}

	return (
		<div className="min-h-screen bg-background text-foreground flex">
			{/* Left panel — brand + value prop */}
			<div className="hidden lg:flex lg:w-[52%] flex-col justify-between p-12 bg-card border-r border-border relative overflow-hidden">
				<div className="absolute inset-0 pointer-events-none">
					<div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
					<div className="absolute bottom-0 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
				</div>

				<div className="relative">
					<div className="flex items-center gap-2.5 mb-16">
						<ChittravanLogoMark size={32} />
						<span className="text-foreground text-xl font-semibold tracking-tight">
							Chitravan
						</span>
					</div>

					<div className="mb-12">
						<h1 className="text-4xl font-bold text-foreground leading-tight mb-4">
							Create videos
							<br />
							<span className="text-primary">that tell your story.</span>
						</h1>
						<p className="text-muted-foreground text-lg leading-relaxed max-w-md">
							AI-powered video creation with full editorial control. Generate
							everything or bring your own assets — your workflow, your way.
						</p>
					</div>

					<div className="space-y-6">
						{FEATURES.map((f) => (
							<div key={f.title} className="flex items-start gap-4">
								<div className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
									<span aria-hidden className="text-sm font-bold">
										✦
									</span>
								</div>
								<div>
									<p className="text-foreground font-medium text-sm mb-0.5">
										{f.title}
									</p>
									<p className="text-muted-foreground text-sm leading-relaxed">
										{f.desc}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>

				<div className="relative">
					<p className="text-muted-foreground text-xs">
						{SIGNUP_CREDITS} free credits on signup · No card required
					</p>
				</div>
			</div>

			{/* Right panel — form */}
			<div className="flex-1 flex items-center justify-center px-6 py-12">
				<div className="w-full max-w-sm">
					{/* Mobile logo */}
					<div className="flex items-center gap-2 mb-8 lg:hidden">
						<ChittravanLogoMark size={28} />
						<span className="text-foreground text-lg font-semibold tracking-tight">
							Chitravan
						</span>
					</div>

					<div className="mb-8">
						<h2 className="text-2xl font-bold text-foreground mb-1">
							Welcome back
						</h2>
						<p className="text-muted-foreground text-sm">
							Sign in to your account to continue.
						</p>
					</div>

					{error && (
						<div className="mb-5 px-4 py-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
							{error}
						</div>
					)}

					{/* Google Identity Services renders its official button into this slot.
					    When no client id is available at build time the slot stays empty
					    and the divider+form below carry the full sign-in flow. */}
					{GOOGLE_CLIENT_ID ? (
						<>
							<div
								ref={googleBtnRef}
								className="flex justify-center [color-scheme:light]"
								aria-busy={googleLoading}
							/>
							<div className="flex items-center gap-3 my-5">
								<div className="flex-1 h-px bg-border" />
								<span className="text-muted-foreground text-xs">or</span>
								<div className="flex-1 h-px bg-border" />
							</div>
						</>
					) : null}

					<form onSubmit={handleCredentials} className="space-y-4">
						<div>
							<label className="block text-xs font-medium text-muted-foreground mb-1.5">
								Email
							</label>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								placeholder="you@example.com"
								className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
							/>
						</div>
						<div>
							<div className="flex items-center justify-between mb-1.5">
								<label className="text-xs font-medium text-muted-foreground">
									Password
								</label>
								<Link
									href="/auth/forgot-password"
									className="text-xs text-primary hover:underline"
								>
									Forgot password?
								</Link>
							</div>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								placeholder="••••••••"
								className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
							/>
						</div>
						<button
							type="submit"
							disabled={loading || googleLoading}
							className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-sm px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
						>
							{loading && (
								<svg
									className="animate-spin h-4 w-4"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									/>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
									/>
								</svg>
							)}
							Sign In
						</button>
					</form>

					<p className="mt-6 text-center text-xs text-muted-foreground">
						No account?{" "}
						<Link
							href="/register"
							className="text-primary hover:underline font-medium"
						>
							Create one free →
						</Link>
					</p>
				</div>
			</div>
		</div>
	);
}

export default function LoginPage() {
	return (
		<Suspense fallback={null}>
			<LoginInner />
		</Suspense>
	);
}
